// src/contexts/OnboardingContext.tsx
import React, { createContext, useContext, useReducer, useEffect } from 'react';

interface Organisation {
  name: string;
  phoneNumber: string;
  verificationEmails: string[];
  shortname: string;
}

interface Administrator {
  fullName: string;
  email: string;
  phoneNumber: string;
  username: string;
  password: string;
}

interface Charity {
  name: string;
  billableRules: string;
}

interface ValidationError {
  field: string;
  message: string;
}

export interface OnboardingState {
  token: string;
  companyLogo?: string;
  activeStep: number;
  validationErrors: Record<string, ValidationError[]>;
  organisation: Organisation;
  administrator: Administrator;
  charities: Charity[];
  // Track whether each step has been visited/modified
  stepsVisited: number[];
  isSubmitting: boolean;
}

type Action =
  | { type: 'SET_TOKEN'; payload: string }
  | { type: 'SET_COMPANY_LOGO'; payload: string }
  | { type: 'SET_ACTIVE_STEP'; payload: number }
  | { type: 'UPDATE_ORGANISATION'; payload: Partial<Organisation> }
  | { type: 'UPDATE_ADMINISTRATOR'; payload: Partial<Administrator> }
  | { type: 'ADD_CHARITY' }
  | { type: 'UPDATE_CHARITY'; payload: { index: number; charity: Partial<Charity> } }
  | { type: 'REMOVE_CHARITY'; payload: number }
  | { type: 'ADD_VALIDATION_ERROR'; payload: { step: string; error: ValidationError } }
  | { type: 'CLEAR_VALIDATION_ERRORS'; payload: string }
  | { type: 'MARK_STEP_VISITED'; payload: number }
  | { type: 'SET_SUBMITTING'; payload: boolean }
  | { type: 'RESET_STATE' };

const initialState: OnboardingState = {
  token: '',
  activeStep: 0,
  validationErrors: {},
  organisation: {
    name: '',
    phoneNumber: '',
    verificationEmails: [],
    shortname: '',
  },
  administrator: {
    fullName: '',
    email: '',
    phoneNumber: '',
    username: '',
    password: '',
  },
  charities: [],
  stepsVisited: [],
  isSubmitting: false,
};

const OnboardingContext = createContext<{
  state: OnboardingState;
  dispatch: React.Dispatch<Action>;
} | null>(null);

const onboardingReducer = (state: OnboardingState, action: Action): OnboardingState => {
  switch (action.type) {
    case 'SET_TOKEN':
      return { ...state, token: action.payload };

    case 'SET_COMPANY_LOGO':
      return { ...state, companyLogo: action.payload };

    case 'SET_ACTIVE_STEP':
      return { ...state, activeStep: action.payload };

    case 'UPDATE_ORGANISATION':
      return {
        ...state,
        organisation: { ...state.organisation, ...action.payload },
      };

    case 'UPDATE_ADMINISTRATOR':
      return {
        ...state,
        administrator: { ...state.administrator, ...action.payload },
      };

    case 'ADD_CHARITY':
      return {
        ...state,
        charities: [...state.charities, { name: '', billableRules: '' }],
      };

    case 'UPDATE_CHARITY':
      return {
        ...state,
        charities: state.charities.map((charity, index) =>
          index === action.payload.index
            ? { ...charity, ...action.payload.charity }
            : charity
        ),
      };

    case 'REMOVE_CHARITY':
      return {
        ...state,
        charities: state.charities.filter((_, index) => index !== action.payload),
      };

    case 'ADD_VALIDATION_ERROR':
      return {
        ...state,
        validationErrors: {
          ...state.validationErrors,
          [action.payload.step]: [
            ...(state.validationErrors[action.payload.step] || []),
            action.payload.error,
          ],
        },
      };

    case 'CLEAR_VALIDATION_ERRORS':
      const { [action.payload]: _, ...remainingErrors } = state.validationErrors;
      return {
        ...state,
        validationErrors: remainingErrors,
      };

    case 'MARK_STEP_VISITED':
        return {
            ...state,
            // Only add the step if it's not already in the array
            stepsVisited: state.stepsVisited.includes(action.payload) 
              ? state.stepsVisited 
              : [...state.stepsVisited, action.payload],
          };

    case 'SET_SUBMITTING':
      return {
        ...state,
        isSubmitting: action.payload,
      };

    case 'RESET_STATE':
      return {
        ...initialState,
        token: state.token, // Preserve the token
        companyLogo: state.companyLogo, // Preserve the logo
      };

    default:
      return state;
  }
};

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(onboardingReducer, initialState);

  // Load state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('onboardingState');
    if (savedState && state.token) { // Only restore if we have a valid token
      try {
        const parsedState = JSON.parse(savedState);
        // Batch all updates in a single render
        const updateState = () => {
          // Update organisation
          Object.entries(parsedState.organisation || {}).forEach(([key, value]) => {
            dispatch({
              type: 'UPDATE_ORGANISATION',
              payload: { [key]: value },
            });
          });
          // Update administrator
          Object.entries(parsedState.administrator || {}).forEach(([key, value]) => {
            dispatch({
              type: 'UPDATE_ADMINISTRATOR',
              payload: { [key]: value },
            });
          });
          // Update charities
          parsedState.charities?.forEach((charity: Charity) => {
            dispatch({ type: 'ADD_CHARITY' });
          });
          // After adding all charities, update them with actual data
          parsedState.charities?.forEach((charity: Charity, index: number) => {
            dispatch({
              type: 'UPDATE_CHARITY',
              payload: {
                index,
                charity,
              },
            });
          });
        };

        updateState();
      } catch (error) {
        console.error('Error restoring onboarding state:', error);
      }
    }
  }, [state.token]); 
  
  // Save state to localStorage when it changes
  useEffect(() => {
    if (state.token) { // Only save if we have a valid token
      localStorage.setItem('onboardingState', JSON.stringify({
        organisation: state.organisation,
        administrator: state.administrator,
        charities: state.charities,
      }));
    }
  }, [state.organisation, state.administrator, state.charities, state.token]);

  return (
    <OnboardingContext.Provider value={{ state, dispatch }}>
      {children}
    </OnboardingContext.Provider>
  );
};

// Custom hook for accessing the onboarding context
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const hasStepBeenVisited = (state: OnboardingState, step: number): boolean => {
    return state.stepsVisited.includes(step);
};